import { useEffect, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { ValBadge } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Plane, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Leaves() {
  const { role } = useAuth();
  const [items, setItems] = useState(null);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ type_conge: "congé", date_debut: "", date_fin: "", motif: "" });
  const canDecide = ["super_admin", "direction_generale", "responsable", "direction_commerciale"].includes(role);

  const load = () => api.get("/leaves").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings").then((r) => setTypes(r.data.leave_types || [])); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/leaves", f); toast.success("Demande envoyée"); setOpen(false); setF({ type_conge: "congé", date_debut: "", date_fin: "", motif: "" }); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const decide = async (id, statut) => { try { await api.patch(`/leaves/${id}/decision`, { statut }); toast.success(`Demande ${statut.toLowerCase()}e`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <PageHeader title="Congés & Absences" subtitle="Demandes et validation — Collaborateur → Responsable → Décision">
        <Button onClick={() => setOpen(true)} data-testid="add-leave" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle demande</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Plane} title="Aucune demande" subtitle="Soumettez une demande de congé, absence, mission ou formation." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Collaborateur</TableHead><TableHead>Type</TableHead><TableHead>Du</TableHead><TableHead>Au</TableHead><TableHead>Motif</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{l.user_name}</TableCell>
                  <TableCell className="capitalize">{l.type_conge}</TableCell>
                  <TableCell className="text-sm">{fmtDate(l.date_debut)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(l.date_fin)}</TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-40 truncate">{l.motif || "—"}</TableCell>
                  <TableCell><StatusBadge value={l.statut} /><ValBadge v={l.validation} />{l.decided_by && <span className="block text-[10px] text-slate-400 mt-0.5">par {l.decided_by}</span>}</TableCell>
                  <TableCell>
                    {canDecide && l.statut === "En attente" && (
                      <div className="flex gap-1.5">
                        <Button size="sm" onClick={() => decide(l.id, "Approuvé")} className="bg-emerald-600 text-white h-8 px-2" data-testid={`approve-${l.id}`}><Check className="h-4 w-4" /></Button>
                        <Button size="sm" onClick={() => decide(l.id, "Refusé")} className="bg-rose-600 text-white h-8 px-2" data-testid={`refuse-${l.id}`}><X className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Nouvelle demande</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label className="text-xs">Type</Label>
              <Select value={f.type_conge} onValueChange={(v) => setF({ ...f, type_conge: v })}><SelectTrigger className="mt-1" data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date de début</Label><Input type="date" value={f.date_debut} onChange={(e) => setF({ ...f, date_debut: e.target.value })} required className="mt-1" data-testid="leave-debut" /></div>
              <div><Label className="text-xs">Date de fin</Label><Input type="date" value={f.date_fin} onChange={(e) => setF({ ...f, date_fin: e.target.value })} required className="mt-1" data-testid="leave-fin" /></div>
            </div>
            <div><Label className="text-xs">Motif</Label><Textarea value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} rows={2} className="mt-1" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" disabled={saving} className="bg-brand-600 text-white" data-testid="leave-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
